<template>
  <div class="consulting-container">
    <div class="consulting-wrapper">
      <!-- 左侧留言列表 -->
      <div class="message-list-panel">
        <div class="panel-header">
          <h3>咨询客户</h3>
        </div>
        <div class="message-list">
          <div
            v-for="item in messageList"
            :key="item.user_id"
            class="message-item"
            :class="{ active: selectedUserId === item.user_id }"
            @click="selectUser(item)"
          >
            <div class="avatar-container">
              <img :src="getImageUrl(item.avatar)" :alt="item.nickname" class="user-avatar">
            </div>
            <div class="message-content">
              <div class="user-info">
                <span class="nickname">{{ item.nickname }}</span>
                <span class="message-time">{{ item.time_text }}</span>
              </div>
              <div class="latest-message">{{ item.latest_message }}</div>
            </div>
            <div v-if="item.has_unread" class="unread-badge">
              {{ item.unread_count }}
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧对话详情 -->
      <div class="conversation-panel">
        <div v-if="selectedUser" class="conversation-header">
          <div class="user-info">
            <img :src="getImageUrl(selectedUser.avatar)" :alt="selectedUser.nickname" class="user-avatar">
            <span class="nickname">{{ selectedUser.nickname }}</span>
          </div>
        </div>

        <div ref="conversationContent" class="conversation-content">
          <!-- 加载更多提示 -->
          <div v-if="loading && pagination.page > 1" class="loading-more">
            <i class="el-icon-loading" />
            <span>加载更多消息...</span>
          </div>

          <div v-if="messages.length === 0 && !loading" class="empty-message">
            <p>暂无对话记录</p>
          </div>
          <div v-else class="messages-container">
            <div
              v-for="message in messages"
              :key="message.id"
              class="message-bubble"
              :class="{ 'user-message': message.is_user, 'other-message': !message.is_user }"
            >
              <div class="message-avatar">
                <img
                  :src="message.is_user ? getImageUrl(userInfo.avatar) : getImageUrl(selectedUser.avatar)"
                  :alt="message.is_user ? userInfo.nickname : selectedUser.nickname"
                  class="avatar"
                >
              </div>
              <div class="message-text">{{ message.content }}</div>
            </div>
          </div>
        </div>

        <div class="message-input">
          <div class="input-tools">
            <i class="el-icon-picture-outline tool-icon" />
            <i class="el-icon-paperclip tool-icon" />
            <i class="el-icon-microphone tool-icon" />
            <i class="el-icon-sunny tool-icon" />
          </div>
          <div class="input-area">
            <el-input
              v-model="inputMessage"
              type="textarea"
              :rows="3"
              placeholder="请输入文字~"
              resize="none"
              @keyup.enter.ctrl="sendMessage"
            />
            <el-button type="primary" :disabled="!inputMessage.trim()" @click="sendMessage">
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMessageBoardList, getConversationDetail } from '@/api/user'
import { mapGetters } from 'vuex'

export default {
  name: 'Consulting',
  data() {
    return {
      messageList: [],
      selectedUserId: null,
      selectedUser: null,
      messages: [],
      inputMessage: '',
      loading: false,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        hasMore: true
      }
    }
  },
  computed: {
    ...mapGetters(['userInfo'])
  },
  created() {
    this.fetchMessageList()
  },
  mounted() {
    // 添加滚动监听
    const container = this.$refs.conversationContent
    if (container) {
      container.addEventListener('scroll', this.handleScroll)
    }
  },
  beforeDestroy() {
    // 移除滚动监听
    const container = this.$refs.conversationContent
    if (container) {
      container.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    // 获取留言列表
    async fetchMessageList() {
      try {
        const response = await getMessageBoardList({
          page: 1,
          limit: 20
        })
        if (response && response.code === 1 && response.data) {
          this.messageList = response.data.list || []
          this.pagination.total = response.data.total || 0
          // 默认选择第一个用户
          if (this.messageList.length > 0) {
            this.selectUser(this.messageList[0])
          }
        }
      } catch (error) {
        console.error('获取留言列表失败:', error)
        this.$message.error('获取留言列表失败')
      }
    },

    // 选择用户
    async selectUser(user) {
      this.selectedUserId = user.user_id
      this.selectedUser = user
      this.messages = []
      this.pagination.page = 1
      this.pagination.hasMore = true
      await this.fetchConversationDetail()
    },

    // 获取对话详情
    async fetchConversationDetail() {
      if (!this.selectedUserId) return

      try {
        this.loading = true
        const response = await getConversationDetail({
          user_id: this.selectedUserId,
          page: this.pagination.page,
          limit: this.pagination.limit
        })

        if (response && response.code === 1 && response.data) {
          const data = response.data
          // 更新选中用户信息
          if (data.other_user) {
            this.selectedUser = data.other_user
          }

          if (this.pagination.page === 1) {
            this.messages = data.messages || []
          } else {
            // 分页加载时，新消息添加到前面
            this.messages = [...(data.messages || []), ...this.messages]
          }

          this.pagination.total = data.total || 0
          this.pagination.hasMore = this.messages.length < this.pagination.total

          // 滚动到底部
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      } catch (error) {
        console.error('获取对话详情失败:', error)
        this.$message.error('获取对话详情失败')
      } finally {
        this.loading = false
      }
    },

    // 加载更多消息
    async loadMoreMessages() {
      if (this.loading || !this.pagination.hasMore) return

      this.pagination.page++
      await this.fetchConversationDetail()
    },

    // 发送消息
    async sendMessage() {
      if (!this.inputMessage.trim()) return

      const messageContent = this.inputMessage.trim()
      this.inputMessage = ''

      // 临时添加到消息列表（用户消息）
      const newMessage = {
        id: Date.now(),
        content: messageContent,
        is_user: true,
        create_time: new Date().toISOString()
      }
      this.messages.push(newMessage)

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      // TODO: 调用发送消息接口
      try {
        // const response = await sendMessage({
        //   user_id: this.selectedUserId,
        //   content: messageContent
        // })
        // if (response && response.code === 1) {
        //   // 发送成功，可以更新消息状态
        // }
      } catch (error) {
        console.error('发送消息失败:', error)
        this.$message.error('发送消息失败')
      }
    },

    // 滚动到底部
    scrollToBottom() {
      const container = this.$refs.conversationContent
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // 处理滚动事件
    handleScroll(event) {
      const container = event.target
      // 滚动到顶部时加载更多消息
      if (container.scrollTop === 0 && this.pagination.hasMore && !this.loading) {
        this.loadMoreMessages()
      }
    },

    // 获取图片URL
    getImageUrl(imagePath) {
      if (!imagePath) return require('@/assets/image/logo.png')
      if (imagePath.startsWith('http')) return imagePath
      if (!imagePath.startsWith('/uploads/')) {
        return `${process.env.VUE_APP_BASE_API}/uploads/${imagePath}`
      }
      return `${process.env.VUE_APP_BASE_API}${imagePath}`
    }
  }
}
</script>

<style scoped>
.consulting-container {
  height: calc(100vh - var(--navbar-total-height, 80px));
  height: calc(100dvh - var(--navbar-total-height, 80px));
  background: #F5F5F5;
  padding: 20px;
}

.consulting-wrapper {
  display: flex;
  height: 100%;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

/* 左侧留言列表 */
.message-list-panel {
  width: 300px;
  border-right: 1px solid #E4E7ED;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 20px;
  border-bottom: 1px solid #E4E7ED;
  background: #F8F9FA;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.message-list {
  flex: 1;
  overflow-y: auto;
}

.message-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  border-bottom: 1px solid #F2F3F5;
  transition: background-color 0.3s;
}

.message-item:hover {
  background: #F8F9FA;
}

.message-item.active {
  background: #E6F7FF;
  border-right: 3px solid #1890FF;
}

.avatar-container {
  margin-right: 12px;
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.message-content {
  flex: 1;
  min-width: 0;
}

.user-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.nickname {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.latest-message {
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread-badge {
  background: #F56C6C;
  color: #fff;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

/* 右侧对话详情 */
.conversation-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.conversation-header {
  padding: 20px;
  border-bottom: 1px solid #E4E7ED;
  background: #F8F9FA;
}

.conversation-header .user-info {
  display: flex;
  align-items: center;
}

.conversation-header .user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.conversation-header .nickname {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.conversation-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #FAFAFA;
}

.empty-message {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.loading-more {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

.loading-more i {
  margin-right: 8px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.messages-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-bubble {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.message-bubble.user-message {
  flex-direction: row-reverse;
}

.message-avatar .avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.message-text {
  max-width: 60%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.user-message .message-text {
  background: #1890FF;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.other-message .message-text {
  background: #fff;
  color: #303133;
  border: 1px solid #E4E7ED;
  border-bottom-left-radius: 4px;
}

/* 消息输入区域 */
.message-input {
  padding: 20px;
  border-top: 1px solid #E4E7ED;
  background: #fff;
}

.input-tools {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.tool-icon {
  font-size: 18px;
  color: #C0C4CC;
  cursor: pointer;
  transition: color 0.3s;
}

.tool-icon:hover {
  color: #606266;
}

.input-area {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.input-area .el-textarea {
  flex: 1;
}

.input-area .el-button {
  height: 40px;
  padding: 0 20px;
}

/* 滚动条样式 */
.message-list::-webkit-scrollbar,
.conversation-content::-webkit-scrollbar {
  width: 6px;
}

.message-list::-webkit-scrollbar-track,
.conversation-content::-webkit-scrollbar-track {
  background: #F5F5F5;
}

.message-list::-webkit-scrollbar-thumb,
.conversation-content::-webkit-scrollbar-thumb {
  background: #C0C4CC;
  border-radius: 3px;
}

.message-list::-webkit-scrollbar-thumb:hover,
.conversation-content::-webkit-scrollbar-thumb:hover {
  background: #909399;
}
</style>
